/**
 * Element stays in DOM (never unmounted) to avoid layout shift; uses
 * Blackbird spring physics for the open/close motion — presets: 'smooth'
 * (default, subtle overshoot), 'snappy' (quick, minimal overshoot), 'bouncy'
 * (playful, noticeable overshoot), 'gentle' (slow, no overshoot).
 *
 * When fully revealed, no directive-imposed clamp or overflow:hidden is left
 * on the element — it lays out at its natural full size.
 *
 * Collapses along height by default and along width on `{ axis: 'width' }` —
 * a rail, a sidebar, an inline drawer. The width axis is the same algorithm
 * read along the other axis, not a second directive: it clamps max-width off
 * scrollWidth, takes the inline padding and margins to zero with it, and
 * nudges on X.
 *
 * @see packages/schmancy/src/utils/animation.ts for the Blackbird system
 */

import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { type ElementPart, type PartInfo, PartType } from 'lit/directive.js'
import { animationFrames, fromEvent, merge, race, Subject, timer } from 'rxjs'
import { filter, take, takeUntil } from 'rxjs/operators'
import {
	type AnimationPreset,
	getEasing,
	SPRING_BOUNCY,
	SPRING_GENTLE,
	SPRING_SMOOTH,
	SPRING_SNAPPY,
} from '@schmancy/utils/animation'
import { reducedMotion$ } from './reduced-motion'

export type RevealPreset = 'smooth' | 'snappy' | 'bouncy' | 'gentle'

/** The axis the element collapses along. */
export type RevealAxis = 'height' | 'width'

interface RevealMotionOptions {
	/** Animation preset (default: 'smooth') */
	preset?: RevealPreset
	/** Custom duration override in ms (uses preset duration if not specified) */
	duration?: number
	/** Custom easing override (uses preset easing if not specified) */
	easing?: string
}

/**
 * A discriminated union rather than one bag with both caps, so the cap that
 * does not apply to the chosen axis is a type error at the call site instead
 * of an option that is silently ignored at runtime. `axis` is optional on the
 * height member, which is what leaves every existing call — none of which
 * names an axis — matching exactly the shape it always matched.
 */
export type RevealOptions =
	| (RevealMotionOptions & {
			axis?: 'height'
			/**
			 * Explicit scroll cap (opt-in). When set the element is constrained to
			 * this height with overflow:auto — content scrolls rather than clips.
			 * When omitted (the default) the element is fully unconstrained after
			 * opening: no max-height, no overflow:hidden left on it.
			 */
			maxHeight?: string
			maxWidth?: never
	  })
	| (RevealMotionOptions & {
			axis: 'width'
			/** The width-axis spelling of the cap above. */
			maxWidth?: string
			maxHeight?: never
	  })

/**
 * One collapse algorithm, read along whichever axis the call names — the
 * measurement, the clamp, the two insets that have to reach zero with it, and
 * the direction the closed state nudges toward. A second directive for the
 * width case would be the same two hundred lines of accumulated fixes authored
 * twice and free to drift apart; the axis is a parameter instead.
 */
const AXES = {
	height: {
		clamp: 'maxHeight',
		clampProperty: 'max-height',
		measure: 'scrollHeight',
		insets: ['paddingTop', 'paddingBottom', 'marginTop', 'marginBottom'],
		closedTransform: 'translateY(-8px) scale(0.98)',
		openTransform: 'translateY(0) scale(1)',
	},
	width: {
		clamp: 'maxWidth',
		clampProperty: 'max-width',
		measure: 'scrollWidth',
		insets: ['paddingLeft', 'paddingRight', 'marginLeft', 'marginRight'],
		closedTransform: 'translateX(-8px) scale(0.98)',
		openTransform: 'translateX(0) scale(1)',
	},
} as const satisfies Record<RevealAxis, unknown>

const PRESETS: Record<RevealPreset, AnimationPreset> = {
	smooth: SPRING_SMOOTH,
	snappy: SPRING_SNAPPY,
	bouncy: SPRING_BOUNCY,
	gentle: SPRING_GENTLE,
}

/** Slack over the transition's own duration before the settle signal fires
 *  anyway. A clamp transition whose start and end values happen to be
 *  equal — an empty box, an element in a `display:none` ancestor — never fires
 *  `transitionend` at all, and a consumer waiting on the settle must not wait
 *  forever for it. This is the backstop every consumer would otherwise
 *  hand-pick for itself. */
const SETTLE_BACKSTOP_SLACK_MS = 120

export interface RevealSettledDetail {
	/** The state the element has just settled into. */
	open: boolean
}

/**
 * `reveal-settled` — dispatched on the revealed element itself once it has
 * actually reached its open (or closed) box: after the collapse axis' own
 * clamp transition ends, or immediately when reduced motion left no
 * transition to wait for.
 * Fires once per genuine `show` transition and never on the initial mount,
 * where there is no transition and the element is already settled.
 *
 * This is the completion signal any post-open effect needs — scrolling the
 * element the newly-opened panel just pushed off screen, measuring the settled
 * box — so no consumer has to race `transitionend` against a hand-picked
 * backstop or re-derive this directive's own reduced-motion branch.
 *
 * Bubbles within the tree it was dispatched in, never across a shadow
 * boundary: the element that reveals is the element that announces.
 */
export class RevealSettledEvent extends CustomEvent<RevealSettledDetail> {
	constructor(open: boolean) {
		super('reveal-settled', { detail: { open }, bubbles: true, composed: false })
	}
}

declare global {
	interface HTMLElementEventMap {
		'reveal-settled': RevealSettledEvent
	}
}

class RevealDirective extends AsyncDirective {
	/** The transition string currently written onto the element — the guard that
	 *  keeps a re-derivation from rewriting an identical value. */
	private appliedTransition: string | undefined = undefined
	private teardown$ = new Subject<void>()
	/** Fired at the start of every transition, cancelling whatever the PREVIOUS
	 *  transition still had pending — its deferred open measurement, and its
	 *  own transitionend listener (still armed; its take(1) hadn't fired yet).
	 *  Without it an interrupting collapse's clamp transitionend is
	 *  mistaken for the open transition's, clearing the clamp right after
	 *  collapsing and invisibly re-expanding the element's layout box while it
	 *  stays opacity:0/aria-hidden — and the settle signal announces a state
	 *  the element is no longer in. */
	private settleCancel$ = new Subject<void>()
	/** True if this is the very first update call — used to detect always-true initial mount. */
	private firstUpdate = true
	/** Previous show value — animation only fires on false→true transitions. */
	private prevShow: boolean | undefined = undefined

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) {
			throw new Error('reveal() can only be used on elements')
		}
	}

	render(show?: boolean, options?: RevealOptions) {
		void show
		void options
		return noChange
	}

	override update(part: ElementPart, [show = false, options = {}]: [boolean | undefined, RevealOptions?]) {
		const element = part.element as HTMLElement

		const { preset = 'smooth', duration, easing } = options
		const axis = AXES[options.axis ?? 'height']
		const cap = options.axis === 'width' ? options.maxWidth : options.maxHeight
		const explicitCap = cap !== undefined

		const presetConfig = PRESETS[preset]
		const actualDuration = duration ?? presetConfig.duration
		const actualEasing = easing ?? getEasing(presetConfig)
		const reducedMotion = reducedMotion$.value

		const isFirstUpdate = this.firstUpdate
		this.firstUpdate = false
		const didChange = show !== this.prevShow
		this.prevShow = show

		// Re-derived every update rather than pinned at the first one:
		// `reducedMotion$` is live (the OS preference can flip while the page is
		// open, which is the contract every other physics directive keeps), and a
		// transition baked in at first paint would keep animating for a reader who
		// has since asked it not to. Written only when the derived value actually
		// changes, so an identical re-assignment can never interrupt a transition
		// already running.
		const transition = reducedMotion
			? 'none'
			: [
					`${axis.clampProperty} ${actualDuration}ms ${actualEasing}`,
					`opacity ${actualDuration}ms ${actualEasing}`,
					`transform ${actualDuration}ms ${actualEasing}`,
					`padding ${actualDuration}ms ${actualEasing}`,
					`margin ${actualDuration}ms ${actualEasing}`,
				].join(', ')
		if (transition !== this.appliedTransition) {
			this.appliedTransition = transition
			element.style.transition = transition
		}

		if (show) {
			if (isFirstUpdate) {
				// Always-true initial mount: do not impose a clamp animation.
				// Apply opacity/transform entrance only (visible immediately for
				// reduced-motion; fades in via the transition for normal motion).
				if (explicitCap) {
					element.style[axis.clamp] = cap
					element.style.overflow = 'auto'
				} else {
					element.style[axis.clamp] = ''
					element.style.overflow = ''
				}
				element.style.opacity = '1'
				element.style.transform = axis.openTransform
			} else if (didChange) {
				// Toggle case: false→true. Animate from 0 to natural size,
				// then release the clamp so content is fully unconstrained.
				// Guard: skip when show stays true across re-renders — re-animating
				// every render causes the overlay to resize and flicker on every tick.
				this.settleCancel$.next()

				if (reducedMotion) {
					if (explicitCap) {
						element.style[axis.clamp] = cap
						element.style.overflow = 'auto'
					} else {
						element.style[axis.clamp] = ''
						element.style.overflow = ''
					}
					element.style.opacity = '1'
					element.style.transform = axis.openTransform
					this.announceSettled(element, axis, true, false, actualDuration)
				} else {
					// Force a concrete start value so CSS can animate from it. This
					// lands synchronously, inside the element part's own commit, so a
					// wrapper whose content arrives in this very render is already
					// clamped before the first paint — no flash of full size.
					element.style.overflow = 'hidden'
					element.style[axis.clamp] = '0'
					void element.offsetHeight // force reflow

					if (explicitCap) {
						element.style[axis.clamp] = cap
						element.style.opacity = '1'
						element.style.transform = axis.openTransform
						this.announceSettled(element, axis, true, true, actualDuration, () => {
							element.style.overflow = 'auto'
						})
					} else {
						// The scroll measurement is read one animation frame later, never here.
						// Lit commits an ELEMENT part BEFORE that element's own child
						// parts, and a nested element's first render is scheduled as its
						// own microtask after this one — so a wrapper whose content
						// arrives in this render measures an empty box at commit time,
						// opens to a `0` clamp, never fires `transitionend` (0→0 is
						// no transition), and stays permanently collapsed with nothing
						// thrown. The frame boundary is the first moment the DOM this
						// render produced is complete; a microtask is not, because the
						// nested renders are queued behind it.
						animationFrames()
							.pipe(take(1), takeUntil(merge(this.teardown$, this.settleCancel$)))
							.subscribe(() => {
								element.style[axis.clamp] = `${element[axis.measure]}px`
								element.style.opacity = '1'
								element.style.transform = axis.openTransform
								// Once the open animation settles, release the clamp entirely.
								this.announceSettled(element, axis, true, true, actualDuration, () => {
									element.style[axis.clamp] = ''
									element.style.overflow = ''
								})
							})
					}
				}
			}

			element.style.pointerEvents = ''
			for (const inset of axis.insets) element.style[inset] = ''
			element.removeAttribute('aria-hidden')
			element.removeAttribute('inert')
		} else {
			// The initial mount is a state, not a transition — the element is
			// already settled closed and has nothing to cancel or announce. A
			// re-render at an unchanged `show` starts no new transition either, so
			// it must not cancel the one still running: cancelling without
			// re-arming below would strand a consumer waiting on the settle
			// forever, which is exactly what this directive's own `reveal-settled`
			// contract promises cannot happen.
			const isCollapseTransition = didChange && !isFirstUpdate

			// Cancel a still-pending open measurement and clamp-release (see the
			// `if (show)` branch above) before this collapse's own clamp
			// transition can be mistaken for the open transition it was waiting on.
			if (isCollapseTransition) this.settleCancel$.next()

			// Collapse: animate to 0. Re-measure from the natural size if the
			// clamp was previously released (the clamp is '' or 'none').
			if (element.style[axis.clamp] === '' || element.style[axis.clamp] === 'none') {
				element.style.overflow = 'hidden'
				element.style[axis.clamp] = `${element[axis.measure]}px`
				void element.offsetHeight // force reflow so CSS sees the start value
			} else {
				element.style.overflow = 'hidden'
			}

			element.style[axis.clamp] = '0'
			element.style.opacity = '0'
			element.style.transform = axis.closedTransform
			element.style.pointerEvents = 'none'
			for (const inset of axis.insets) element.style[inset] = '0'
			element.setAttribute('aria-hidden', 'true')
			element.setAttribute('inert', '')

			if (isCollapseTransition) this.announceSettled(element, axis, false, !reducedMotion, actualDuration)
		}

		return noChange
	}

	/**
	 * Runs `release` and announces the settled state once the element has
	 * actually reached it — the clamp's own `transitionend` when a transition is
	 * running, a bounded backstop when one is installed but can never fire (a
	 * 0→0 size, an off-screen box) or when reduced motion removed it entirely.
	 *
	 * The axis travels in rather than being re-derived, because the property
	 * this waits on has to be the one the caller actually animated: watching
	 * `max-height` on a width collapse would wait on a transition that never
	 * runs, and every settle would arrive from the backstop timer instead.
	 */
	private announceSettled(
		element: HTMLElement,
		axis: (typeof AXES)[RevealAxis],
		open: boolean,
		animated: boolean,
		duration: number,
		release?: () => void,
	): void {
		const settled$ = animated
			? race(
					fromEvent<TransitionEvent>(element, 'transitionend').pipe(
						filter(e => e.propertyName === axis.clampProperty && e.target === element),
					),
					timer(duration + SETTLE_BACKSTOP_SLACK_MS),
				)
			: timer(0)

		settled$.pipe(take(1), takeUntil(merge(this.teardown$, this.settleCancel$))).subscribe(() => {
			release?.()
			element.dispatchEvent(new RevealSettledEvent(open))
		})
	}

	override disconnected(): void {
		this.teardown$.next()
	}

	override reconnected(): void {}
}

export const reveal = directive(RevealDirective)
